import React, { useEffect, useState } from 'react';
//import MUI
import { Button, Checkbox, Fab, FormControl, FormControlLabel, FormHelperText, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
// import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';

import moment from 'moment';
import { toast } from 'react-toastify';
import { isAndroid, isFirefox, isIOS, isMobile, isSafari } from 'react-device-detect';

import Spacer from '../../components/shared/Spacer';
import Api from '../../api';
import Edges from '../../components/shared/Edges';
import styled from 'styled-components';
import generateOfficeHoursICal from '../../helpers/officeHoursAppointmentICal';
import * as theme from '../../theme';
import { STUDENT_ROLE_ID } from '../../constants/constants';

//icons and assets
import presencialLogo from '../../assets/presencial.png';
import onlineLogo from '../../assets/online.png';
import unavailableLogo from '../../assets/unavailable.png';
import availableLogo from '../../assets/available.png';
import * as Sentry from '@sentry/react';
import checkmarkLogo from '../../assets/checkmark.png';
import { logUserAction } from '../../services/appServices';
import UserActions from '../../constants/user-actions';
import _ from 'lodash';

/*
 * 1. activeWeek is from the start of the term
 */
const TimeSlotPicker = (props) => {
    const {
        instructorInfo,
        instructorId,
        closeDialog,
        activeWeek,
        termSchoolID,
        termStartDate,
        termEndDate,
        bookingTimeSlots,
        isWeekView,
        onTimeslotBooking
    } = props;

    const customId = 'custom-id-yes';
    const api = new Api();

    const [selectedRole, setSelectedRole] = useState({});
    const [roles, setRoles] = useState([]);
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
    // calculate week from term start date + the user selection within that term - 1
    const [week, setWeek] = useState(moment(termStartDate, 'YYYY-MM-DD').week() + activeWeek - 1);
    const [onlyAvailable, setOnlyAvailable] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const activeButtonColor = activeStep === 2 ? '#32B11E' : '#005FA5';
    const [emailHelperText, setEmailHelperText] = useState('Required');
    const [bookingData, setBookingData] = useState({
        ohId: 0,
        timeSlotIndex: 0,
        name: '',
        email: '',
        bcitId: null,
        topic: null,
        roleId: 0
    });
    const [bookingResult, setBookingResult] = useState({
        isEmailConfirmed: false,
        location: null,
        note: null
    });

    const [selectedTimeslot, setSelectedTimeslot] = useState(null);

    const steps = getSteps();

    const handleNext = async () => {
        const bcitIdRegex = /[aA]\d{8}/;

        if (activeStep === 0) {
            if (!selectedTimeslot) {
                toast.warn('Select a time slot');
                return;
            }
        }

        if (activeStep === 1) {
            if (!selectedRole.id || !bookingData.name || !bookingData.email) {
                toast.warn('Role, Name and Email are required');
                return;
            }

            if (
                selectedRole.id === STUDENT_ROLE_ID &&
                !bookingData.email.trim().toLowerCase().includes('@my.bcit.ca')
            ) {
                toast.warn('Students must user their @my.bcit.ca email');
                return;
            }

            if (bookingData.bcitId && bookingData.bcitId.length !== 9) {
                toast.warn('BCIT ID must be 9 characters');
                return;
            }

            if (bookingData.bcitId && !bcitIdRegex.test(bookingData.bcitId)) {
                toast.warn('BCIT ID must be in the format A12345678');
                return;
            }

            if (bookingData.topic && bookingData.topic.length > 100) {
                toast.warn('Topic must be less than 100 characters');
                return;
            }
        }

        if (activeStep === 2) {
            setIsConfirmDisabled(true);
            try {
                await handleBookTimeSlot();
            } catch (error) {
                Sentry.captureException(error);
                return;
            } finally {
                setIsConfirmDisabled(false);
            }
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleClose = () => {
        closeDialog();
    };

    useEffect(() => {
        const fetchData = async () => {
            const roles = await api.get(`/api/role/get`);
            setRoles(roles);
        };

        fetchData().catch((error) => Sentry.captureException(error));
    }, [instructorId, termSchoolID]);

    function getSteps() {
        return ['Select slot', 'Your information', 'Confirm'];
    }

    function getStepContent(stepIndex) {
        switch (stepIndex) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            default:
                return 'Unknown stepIndex';
        }
    }

    const renderDateRange = (week) => {
        const year = moment(termStartDate, 'YYYY-MM-DD').year();

        const dateRange = `${moment().year(year).week(week).day('Monday').format('MMM DD')} - ${moment()
            .year(year)
            .week(week)
            .day('Friday')
            .format('MMM DD')}`;
        return dateRange;
    };

    const getIcon = (timeSlot) => {
        if (timeSlot.isOnline) {
            return <Icon src={onlineLogo} />;
        } else if (!timeSlot.isOnline) {
            return <Icon src={presencialLogo} />;
        }

        return <Icon src={onlineLogo} />;
    };

    const handleBookTimeSlot = async () => {
        const { status, data } = await api.insert('/api/timeslot/book', bookingData);
        if (status === 200) {
            setBookingResult(data);
            onTimeslotBooking(selectedTimeslot);
        }
    };

    const isTimeslotSelected = (timeslot) => {
        if (!selectedTimeslot) return;
        return timeslot.ohId === selectedTimeslot.ohId && timeslot.timeSlotIndex === selectedTimeslot.timeSlotIndex;
    };

    const getTimeSlotBackgroundColor = (timeSlot) => {
        if (isTimeslotSelected(timeSlot)) return '32B11E';
        if (timeSlot.isCurrentAccountBooking) return '003E6B';
        if (timeSlot.isAvailable) return 'FFFFFF';
        return 'DADADA';
    };

    const getTimeSlotBorderColor = (timeSlot) => {
        if (timeSlot.isAvailable) return '2.5px solid #32B11E';
        return 'none';
    };

    const getTimeSlotTextColor = (timeSlot) => {
        if (isTimeslotSelected(timeSlot)) return 'FFFFFF';
        if (timeSlot.isCurrentAccountBooking) return 'FFFFFF';
        if (timeSlot.isAvailable) return '1D2847';

        return '898989';
    };

    const handleTimeSlotSelect = (item) => {
        const timeSlots = _.cloneDeep(bookingTimeSlots);

        const index = timeSlots.findIndex((t) => t.ohId === item.ohId && t.timeSlotIndex === item.timeSlotIndex);

        if (index === -1) return;

        for (let t of timeSlots) {
            t.isSelected = false;
        }

        timeSlots[index].isSelected = true;

        //populate the object being sent back to the server
        setBookingData({
            ...bookingData,
            ohId: item.ohId,
            timeSlotIndex: item.timeSlotIndex
        });

        setSelectedTimeslot(item);
    };

    const renderTimeSlotItem = (item) => {
        const backgroundColor = getTimeSlotBackgroundColor(item);
        const textColor = getTimeSlotTextColor(item);
        const borderColor = getTimeSlotBorderColor(item);

        return (
            <>
                <TimeSlotItem
                    key={item.timeIn}
                    color={textColor}
                    backgroundColor={backgroundColor}
                    clickable={item.isAvailable}
                    onClick={() => handleTimeSlotSelect(item)}
                    style={{ border: borderColor }}
                >
                    {getIcon(item)}
                    <TimeSlotTime
                        children={`${moment(item.timeIn, 'hh:mm:ss').format('hh:mm A')} - ${moment(
                            item.timeOut,
                            'hh:mm:ss'
                        ).format('hh:mm A')}`}
                    />
                </TimeSlotItem>
            </>
        );
    };

    const renderTimeSlots = (bookingTimeSlots) => {
        if (bookingTimeSlots.length === 0) {
            return (
                <TimeSlot style={{ textAlign: 'center', color: 'grey' }}>
                    {`There are no office hours for week ${renderDateRange(week)}`}
                </TimeSlot>
            );
        }

        let timeSlots = bookingTimeSlots;

        if (isWeekView) timeSlots = bookingTimeSlots.filter((t) => moment(t.date, 'YYYY-MM-DD').week() === week);
        if (onlyAvailable) timeSlots = timeSlots.filter((t) => t.isAvailable);

        let dates = [...new Set(timeSlots.map((t) => t.date))];

        if (!dates) return null;

        return dates.map((d) => {
            const dateTimeSlots = timeSlots.filter((t) => t.date === d);

            dateTimeSlots.sort((a, b) => {
                if (moment(a.timeIn, 'HH:mm:ss').isBefore(moment(b.timeIn, 'HH:mm:ss'))) return -1;
                else if (moment(a.timeIn, 'HH:mm:ss').isAfter(moment(b.timeIn, 'HH:mm:ss'))) 1;
                else return 0;
            });

            let name = moment(d, 'YYYY-MM-DD').format('dddd, MMMM Do');
            // const date = moment(selectedTimeSlot.date, 'YYYY-MM-DD').format('dddd MMMM Do');

            return (
                <TimeSlot key={name}>
                    {/* TODO: Add Day of Week: 'Monday, March 22' */}
                    <TimeSlotHeader children={`${name}`} />
                    <TimeSlotItems>
                        {dateTimeSlots &&
                            dateTimeSlots.map((item) => {
                                return renderTimeSlotItem(item);
                            })}
                    </TimeSlotItems>
                    {/* <DividerLine /> */}
                </TimeSlot>
            );
        });
    };

    const renderStep1 = () => {
        return (
            <>
                <h3 style={{ textAlign: 'center' }}> Select a time slot </h3>
                <Legend>
                    <div>
                        <Icon src={availableLogo} />
                        <Typography children={`Available`} />
                    </div>
                    <div>
                        <Icon src={unavailableLogo} />
                        <Typography children={`Unavailable`} />
                    </div>
                    <div>
                        <Icon src={presencialLogo} />
                        <Typography children={`In-Person`} />
                    </div>
                    <div>
                        <Icon src={onlineLogo} />
                        <Typography children={`On-line`} />
                    </div>
                </Legend>
                <Toolbar>
                    <Pickers>
                        {isWeekView && (
                            <WeekPicker>
                                <WeekContainer>
                                    <div style={{ float: 'left' }}>
                                        <StyledFabLeft size="small" disabled={false} onClick={() => setWeek(week - 1)}>
                                            <ArrowBackIosIcon style={{ color: '#000', width: '20px' }} />
                                        </StyledFabLeft>
                                    </div>
                                    <WeekDisplay>
                                        <Typography>{renderDateRange(week)}</Typography>
                                    </WeekDisplay>
                                    <div style={{ float: 'right' }}>
                                        <StyledFabRight size="small" disabled={false} onClick={() => setWeek(week + 1)}>
                                            <ArrowForwardIosIcon style={{ color: '#000', width: '20px' }} />
                                        </StyledFabRight>
                                    </div>
                                </WeekContainer>
                            </WeekPicker>
                        )}
                        <Line />
                    </Pickers>

                    <AvailableStationsCheckbox
                        control={
                            <Checkbox
                                checked={onlyAvailable}
                                name="only-available-hours"
                                color="primary"
                                onChange={(e) => setOnlyAvailable(e.target.checked)}
                            />
                        }
                        label="Only show available hours"
                    />
                </Toolbar>

                <TimeSlots>{renderTimeSlots(bookingTimeSlots)}</TimeSlots>
            </>
        );
    };

    const handleBookingDataChange = (name) => (event) => {
        const value = event.target.value;

        if (name === 'topic' && value && value.length > 100) {
            toast.warning('Topic must be less than 100 characters', { toastId: customId });
        }

        setBookingData({
            ...bookingData,
            [name]: value
        });
    };

    const handleRoleChange = (event) => {
        const selectedRole = event.target.value;

        if (selectedRole.id === STUDENT_ROLE_ID) {
            setEmailHelperText('Students must use their @my.bcit.ca email');
        } else {
            setEmailHelperText('Required');
        }

        setSelectedRole(selectedRole);
        setBookingData({ ...bookingData, roleId: selectedRole.id });
    };

    const renderStep2 = () => {
        return (
            <>
                <form noValidate autoComplete="off">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box>
                            <StyledFormControl variant="outlined">
                                <InputLabel>Role</InputLabel>
                                <Select
                                    placeholder="Role"
                                    // labelId="simple-select-outlined-label"
                                    id="simple-select-outlined"
                                    variant="outlined"
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                >
                                    {roles.map((role) => {
                                        return (
                                            <MenuItem key={role.name} value={role}>
                                                {role.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <FormHelperText>Required</FormHelperText>
                            </StyledFormControl>
                        </Box>
                        <Box>
                            <StyledFormControl variant="outlined">
                                <InputLabel>Name</InputLabel>
                                <StyledTextField
                                    key={'Name'}
                                    required
                                    placeholder="Name *"
                                    variant="outlined"
                                    value={bookingData.name}
                                    onChange={handleBookingDataChange('name')}
                                    helperText="Required"
                                />
                            </StyledFormControl>
                        </Box>
                        <Box>
                            <StyledFormControl variant="outlined">
                                <InputLabel>Email</InputLabel>
                                <StyledTextField
                                    key={'Email'}
                                    required
                                    placeholder="Email *"
                                    variant="outlined"
                                    value={bookingData.email}
                                    onChange={handleBookingDataChange('email')}
                                    helperText={emailHelperText}
                                />
                            </StyledFormControl>
                        </Box>
                        <Box>
                            <StyledFormControl variant="outlined">
                                <InputLabel>BCIT ID</InputLabel>
                                <StyledTextField
                                    placeholder="BCIT ID"
                                    variant="outlined"
                                    value={bookingData.bcitId}
                                    onChange={handleBookingDataChange('bcitId')}
                                    helperText="Optional"
                                />
                            </StyledFormControl>
                        </Box>
                        <Box>
                            <StyledFormControl variant="outlined">
                                <InputLabel>Topic</InputLabel>
                                <StyledTextField
                                    id="outlined-multiline-static"
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Topic"
                                    onChange={handleBookingDataChange('topic')}
                                    helperText="Optional (Max 100 Characters)"
                                />
                            </StyledFormControl>
                        </Box>
                    </div>
                </form>
            </>
        );
    };

    const handleICalClick = (event) => {
        if (isMobile && isIOS && !isSafari) {
            toast.info('Please use the Safari Browser to download the calendar file.');
        } else if (isMobile && isAndroid && isFirefox) {
            toast.info('Please use another Browser to download the calendar file.');
        } else {
            generateOfficeHoursICal(selectedTimeslot);
        }
        logUserAction(UserActions.OHAICAL);
    };

    const renderStep3 = () => {
        const date = moment(selectedTimeslot.date, 'YYYY-MM-DD').format('dddd MMMM Do');
        const timeIn = moment(selectedTimeslot.timeIn, 'HH:mm:ss');
        const timeOut = moment(selectedTimeslot.timeOut, 'HH:mm:ss');
        const timeInStr = timeIn.format('h:mm A');
        const timeOutStr = timeOut.format('h:mm A');

        return (
            <ConfirmationContainer>
                <h3 style={{ textAlign: 'center' }}>Confirm Your  {selectedTimeslot.isOnline ? 'Online' : 'In-Person'} Appointment</h3>
                <div>
                    <ConfirmationHeading>Instructor</ConfirmationHeading>
                    <StyledConfirmationInfo>{instructorInfo}</StyledConfirmationInfo>
                    {/* <Typography variant={'subtitle2'}>{minutesStr}</Typography> */}
                </div>

                <Spacer mt={20}>
                    <ConfirmationHeading>Date and time</ConfirmationHeading>
                    <StyledConfirmationInfo>{date}</StyledConfirmationInfo>
                    <StyledConfirmationInfo>
                        {timeInStr} - {timeOutStr}
                    </StyledConfirmationInfo>
                </Spacer>

                <Spacer mt={20}>
                    <ConfirmationHeading>Appointment Details</ConfirmationHeading>
                    <StyledConfirmationInfo>
                        {selectedTimeslot.isOnline ? 'Online appointment (unless instructor indicates otherwise)' : 'In-person appointment (unless instructor indicates otherwise)'}
                    </StyledConfirmationInfo>
                </Spacer>

                <Spacer mt={20}>
                    <ConfirmationHeading>Note</ConfirmationHeading>
                    <StyledNotesInfo>{selectedTimeslot.note}</StyledNotesInfo>
                </Spacer>
            </ConfirmationContainer>
        );
    };

    // TODO: Remove styled stepper on last step- after confirmation
    return (
        <>
            <Container>
                <Edges size="lg">
                    <div>
                        {activeStep !== steps.length ? (
                            <div>
                                <Headline children={instructorInfo} color="primary" />
                                <StyledStepper activeStep={activeStep} alternativeLabel>
                                    {steps.map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{label}</StepLabel>
                                        </Step>
                                    ))}
                                </StyledStepper>
                            </div>
                        ) : (
                            // Confirmation message with removed stepper and instructor info headline
                            <div />
                        )}
                        <div>
                            {activeStep === steps.length ? (
                                <div style={{ backgroundColor: 'white' }}>
                                    <ConfirmationContainer>
                                        <Spacer mt={20} mb={20}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Icon
                                                    src={checkmarkLogo}
                                                    style={{
                                                        marginTop: '5px'
                                                    }}
                                                />
                                                <Typography
                                                    variant={'h6'}
                                                    style={{
                                                        textAlign: 'center',
                                                        marginBottom: '20px'
                                                    }}
                                                >
                                                    {'Thanks for booking!'}
                                                </Typography>
                                            </div>

                                            {bookingResult.isEmailConfirmed ? (
                                                <Typography variant={'h6'}>
                                                    {!bookingResult.location ? (
                                                        <Typography variant={'h6'}>
                                                            You have successfully booked an {selectedTimeslot.isOnline ? 'online' : 'in-person'} office hour appointment.
                                                            <AppointmentInfoContainer>
                                                                <span> {selectedTimeslot.isOnline ? 'Appointment Url' : 'Location'}:</span> The instructor has not setup a url or meeting location.
                                                            </AppointmentInfoContainer>
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant={'h6'}>
                                                            You have successfully booked an {selectedTimeslot.isOnline ? 'online' : 'in-person'} office hour appointment.
                                                            <AppointmentInfoContainer>
                                                                <span>{selectedTimeslot.isOnline ? 'Appointment Url' : 'Location'}:</span> {bookingResult.location}
                                                            </AppointmentInfoContainer>
                                                        </Typography>
                                                    )}
                                                    {bookingResult.note && (
                                                        <AppointmentInfoContainer>
                                                            <span>Note from Instructor:</span> {bookingResult.note}
                                                        </AppointmentInfoContainer>
                                                    )}
                                                </Typography>
                                            ) : (
                                                <Typography variant={'h6'}>
                                                    Please wait for a confirmation email and confirm your email in the next hour using the link provided.
                                                </Typography>
                                            )}
                                        </Spacer>
                                        <ButtonContainer>
                                            <Button variant="contained" onClick={handleClose}>
                                                Close
                                            </Button>
                                            {bookingResult.isEmailConfirmed && (
                                                <AddToCalendarButton
                                                    onClick={handleICalClick}
                                                    children={'Add to Calendar'}
                                                />
                                            )}
                                        </ButtonContainer>
                                    </ConfirmationContainer>
                                </div>
                            ) : (
                                <div style={{ backgroundColor: 'white' }}>
                                    {getStepContent(activeStep)}
                                    <div
                                        style={{
                                            marginTop: '1em',
                                            marginBottom: '0px',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Button disabled={activeStep === 0} onClick={handleBack}>
                                            Back
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleNext}
                                            disabled={isConfirmDisabled}
                                            style={{ backgroundColor: activeButtonColor }}
                                        >
                                            {activeStep === steps.length - 1 ? 'Confirm' : 'Next'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Edges>
            </Container>
        </>
    );
};

const Box = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80%;
    margin: 10px 0px;
`;

const Icon = styled.img`
    width: 25px;
    height: 25px;

    @media (min-width: 768px) {
        margin: 0 8px 0 0;
    }
`;

const Container = styled.div`
    padding: 20px 0;
    @media (min-width: 768px) {
        padding: 20px 0 40px 0;
    }
`;

const InputLabel = styled(Typography)`
    && {
        font-size: 12px;
        margin: 0px 0px 3px 5px;
        text-align: left;
    }
`;

const Headline = styled(Typography)`
    && {
        background-color: white;
        text-align: center;
        font-weight: bold;
        text-transform: uppercase;
        font-size: 20px;

        @media (max-width: 768px) {
            font-size: 16px;
        }
    }
`;

const StyledStepper = styled(Stepper)`
    && {
        margin: 5px 0;
        padding: 24px 0px !important;
    }
`;

const Toolbar = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 30px;
`;

const Pickers = styled.div`
    order: 2;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        margin: auto;
    }

    @media (min-width: 768px) {
        width: 100%;
        margin-bottom: 10px;
        order: 1;
    }
`;

const WeekPicker = styled(FormControl)`
    && {
        @media (min-width: 768px) {
            //FIXME: used to align content in the middle
            margin-left: 10%;
            margin-bottom: 0;
            width: 400px;
        }

        @media (max-width: 768px) {
            margin-bottom: 25px;
            width: 250px;
        }
    }
`;

const WeekContainer = styled.div`
    display: flex;
`;

const WeekDisplay = styled.div`
    margin: auto;
`;

const StyledFabLeft = styled(Fab)`
    && {
        width: 30px;
        height: 30px;
        min-height: auto;
        box-shadow: none;
        background-color: #fff;
        // padding-left: 3px;
        // background-color: #5c5c5c;
    }
`;

const StyledFabRight = styled(Fab)`
    && {
        width: 30px;
        height: 30px;
        min-height: auto;
        box-shadow: none;
        background-color: #fff;
        // padding-left: 3px;
        // background-color: #5c5c5c;
    }
`;

const Legend = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 5px 10px;
    border: 1px solid #cfcfcf;
    border-radius: 10px;
    margin: 0 0 20px 0;
    order: 6;

    @media (min-width: 768px) {
        order: 2;
        height: 56px;
    }

    > div {
        display: flex;
        width: 50%;
        margin: 12px 0;

        @media (min-width: 768px) {
            width: auto;
            margin-right: 10px;

            img {
                margin-right: 5px;
            }
        }

        @media (max-width: 768px) {
            img {
                margin-right: 5px;
            }
        }

        @media (max-width: 320px) {
            width: 100%;

            img {
                margin-right: 5px;
            }
        }

        &:last-child {
            margin-right: 0;
        }

        svg {
            margin-right: 10px;
        }

        p {
            white-space: nowrap;
        }
    }
`;

const AvailableStationsCheckbox = styled(FormControlLabel)`
    && {
        order: 3;
        margin: auto;
    }
`;

const TimeSlots = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const TimeSlot = styled.div`
    margin: auto;
    width: 100%;
    border-radius: 10px;
    margin-bottom: 30px;

    @media (min-width: 640px) {
        width: calc(75% - 15px);
    }

    @media (min-width: 768px) {
        padding: 20px;
        border: 1px solid #c4c4c4;
    }

    @media (min-width: 960px) {
        width: calc(80% - 15px);
    }

    @media (min-width: 1280px) {
        width: calc(90% - 15px);
    }
`;

const TimeSlotHeader = styled(Typography)`
    && {
        margin-bottom: 20px;
        text-align: center;
        font-weight: bold;
    }
`;

const TimeSlotItems = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    @media (min-width: 768px) {
        display: block;
    }
`;

const TimeSlotItem = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    pointer-events: ${(props) => (props.clickable ? 'auto' : 'none')};
    cursor: ${(props) => (props.clickable ? 'pointer' : 'not-allowed')};
    background-color: ${(props) => '#' + props.backgroundColor};
    color: ${(props) => '#' + props.color};
    /* border: 1px solid #7386a0; */
    border-radius: 4px;
    padding: 10px;

    @media (min-width: 768px) {
        border: none;
    }

    svg {
        margin-right: 10px;
    }
    img {
        margin: 0 15px 0 5px;
    }
`;

const TimeSlotTime = styled(Typography)`
    && {
        width: 155px;
        padding: 15px 0;
    }
`;

const ConfirmationHeading = styled(Typography)`
    color: #0079d9;
    font-weight: 600 !important;
`;

const StyledTextField = styled(TextField)`
    /* width: 80%; */
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const StyledConfirmationInfo = styled(Typography)`
    && {
        font-size: 18px;
        color: #21406a;
    }
`;
const StyledNotesInfo = styled(Typography)`
    && {
        font-size: 18px;
        color: #21406a;
        margin-bottom: 1em;
    }
`;

const ConfirmationContainer = styled.div`
    width: 90%;
    margin: auto;
    padding-bottom: 5px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    button {
        width: 100px;
        text-align: center;
        padding: 10px;
        background-color: #0062a8;
        color: #fff;
        &:hover {
            background-color: #258ed9;
        }
    }
`;

const AppointmentInfoContainer = styled.div`
    font-size: 16px;
    margin-top: 1em;
    span {
        color: ${theme.colors.primary};
        font-weight: 600;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-top: 1em;
    flex-direction: column-reverse;
    @media (min-width: 640px) {
        flex-direction: row;
    }
`;

/* Override the  align="center" in the <Grid> component */
const StyledFormControl = styled(FormControl)`
    && {
        // text-align: left;
        width: 100%;
        @media (max-width: 768px) {
            width: 100% !important;
        }
    }
`;

const AddToCalendarButton = styled(Button)`
    && {
        /* margin-left: 15px; */
        background-color: ${theme.colors.active};
        margin-bottom: 1em;
        color: white;
        width: 200px;
        box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
            0px 1px 5px 0px rgba(0, 0, 0, 0.12);

        &:hover,
        &:focus {
            background-color: #043e8a;
        }
        @media (min-width: 640px) {
            margin: 0;
        }
    }
`;

const Line = styled(Divider)`
    && {
        display: none;
    }
    @media (min-width: 640px) {
        margin: 2em 0 0 0;
        display: flex;
    }
`;

export default TimeSlotPicker;
